import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { Handler, makeHandlerChainAwaitable, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { addToChainAfter } from "@insite/client-framework/HandlerCreator";
import { CatalogPage } from "@insite/client-framework/Services/CategoryService";
import * as ProductServiceV2 from "@insite/client-framework/Services/ProductServiceV2";
import {
    chain,
    DispatchSetParameter,
    LoadRealTimeInventory,
    LoadRealTimePrices,
    SetExpandTokens,
} from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import { ProductFilters } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import { ProductModel, RealTimePricingModel } from "@insite/client-framework/Types/ApiModels";
import productGroupingLoadProducts from "@WhiteCap/Handlers/ProductGrouping/ProductGroupingLoadProduct";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import isSerchPage, { isBrickSelectorPage } from "@WhiteCap/Utils/CatalogView";




import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";


import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";





interface Parameter {
    queryString?: string;
    path: string;
    isSearch: boolean;
}

export interface DisplayProductsResult {
    products?: ProductModel[];
    productInfosByProductId: SafeDictionary<ProductInfo>;
    productFilters: ProductFilters;
    isFiltered: boolean;
    unfilteredApiParameter?: ProductServiceV2.GetProductCollectionApiV2Parameter;
    filteredApiParameter?: ProductServiceV2.GetProductCollectionApiV2Parameter;
    catalogPage?: CatalogPage;
}

interface Props {
    apiParameter: ProductServiceV2.GetProductCollectionApiV2Parameter;
    idByPath?: SafeDictionary<string>;
    result: DisplayProductsResult;
    pricingLoaded?: true;
    expandTokens?: ProductServiceV2.ProductExpandTokens;
}

type HandlerType = Handler<Parameter, Props>;

export const AddToExpandTokens: HandlerType = props => {
    if (props.expandTokens) {
        props.expandTokens.push("properties");
    }
};

// EC-849-Create a custom redux store to store the data
export const ProductGroupingApiCall: HandlerType = props => {

    if (typeof window === "undefined") {
        return;
    }
    if(isBrickSelectorPage()){
        return;
    }
    const state = props.getState() as CustomApplicationState;
    const isInSerchPage = isSerchPage();
    const pathname = typeof window !== "undefined" ? window.location.pathname : "";
    const isBrowser = typeof window !== "undefined";
    
    const attributeValueIds = props.apiParameter.attributeValueIds;
    const sortOrder = props.apiParameter.sort;

    //EC-936: Optimize/Prevent API's call in catalog view for product grouping
    // pervent unwanted api call on catalog view by checking same path and product is empty in store
    const storedPathName = localStorage.getItem("pathname");
    const storedAttributeValueIds = localStorage.getItem("attributeValueIds");
    const storedSortOrder = localStorage.getItem("sortOrder");

    const parsedAttributeValueIds = storedAttributeValueIds ? JSON.parse(storedAttributeValueIds) : null;
    const isProducts = state.WhiteCap.products.products;

    let isMatchAttributeValueIds = attributeValueIds ? false : true;
    //EC-936: Trigger an API call when sort is modified
    let isMatchSortOrder = sortOrder ? false : true;
    if(sortOrder && storedSortOrder && sortOrder === storedSortOrder){
        isMatchSortOrder = true
    }
    if (isMatchAttributeValueIds) {
        isMatchAttributeValueIds = parsedAttributeValueIds ? false : true;
    }
    if (
        typeof parsedAttributeValueIds === "object" &&
        typeof attributeValueIds === "object" &&
        parsedAttributeValueIds != null &&
        attributeValueIds != null
    ) {
        function arraysMatch(attributeValueIds: string[], parsedAttributeValueIds: string[]) {
            if (attributeValueIds.length !== parsedAttributeValueIds.length) return false;
            let sortedattributeValueIds = attributeValueIds.slice().sort();
            let sortedparsedAttributeValueIds = parsedAttributeValueIds.slice().sort();
            return sortedattributeValueIds.every(
                (value: string, index: number) => value === sortedparsedAttributeValueIds[index],
            );
        }

        isMatchAttributeValueIds = arraysMatch(attributeValueIds, parsedAttributeValueIds);
    }

    if (isProducts && isBrowser) {
        localStorage.setItem("pathname", pathname);
        sortOrder ? localStorage.setItem("sortOrder", sortOrder) : localStorage.removeItem("sortOrder");
        if (attributeValueIds) {
            localStorage.setItem("attributeValueIds", JSON.stringify(attributeValueIds));
        } else {
            localStorage.removeItem("attributeValueIds");
        }
        if (storedPathName === pathname && isMatchAttributeValueIds && isMatchSortOrder) {
            return;
        }
    }

    if (
        state.context.settings.properties &&
        state.context.settings.properties.enableCatalogView &&
        state.context.settings.properties.enableCatalogView.toLowerCase() === "true" &&
        !isInSerchPage
    ) {
        localStorage.setItem("pathname", pathname);
        sortOrder ? localStorage.setItem("sortOrder", sortOrder) : localStorage.removeItem("sortOrder");
        attributeValueIds
            ? localStorage.setItem("attributeValueIds", JSON.stringify(attributeValueIds))
            : localStorage.removeItem("attributeValueIds");

        props.dispatch(productGroupingLoadProducts(props.apiParameter as any));
    }
};  

export const LoadRealTimePricesExtended: HandlerType = async props => {
    if(isBrickSelectorPage()){
        return;
    }
    const {
        result: { productInfosByProductId },
    } = props;

    const productIds = Object.keys(productInfosByProductId).filter(o => !productInfosByProductId[o]?.pricing);
    if (productIds.length === 0) {
        return;
    }

    const loadRealTimePricingParameter: Parameters<typeof loadRealTimePricing>[0] = {
        productPriceParameters: productIds.map(o => productInfosByProductId[o]!),
        onSuccess: realTimePricing => {
            props.dispatch({
                type: "Pages/ProductList/CompleteLoadRealTimePricing",
                realTimePricing,
            });
            props.pricingLoaded = true;
        },
        onError: () => {
            props.dispatch({
                type: "Pages/ProductList/FailedLoadRealTimePricing",
            });
            props.pricingLoaded = true;
        },
        onComplete(realTimePricingProps) {
            if (realTimePricingProps.apiResult) {
                this.onSuccess?.(realTimePricingProps.apiResult);
            } else if (realTimePricingProps.error) {
                this.onError?.(realTimePricingProps.error);
            }
        },
    };

    const settings = getSettingsCollection(props.getState());

    if (IS_SERVER_SIDE && getIsWebCrawler() && !settings.websiteSettings.includeRealTimeInventoryAndPricingForSeo) {
        const awaitableLoadRealTimePricing = makeHandlerChainAwaitable<
            Parameters<typeof loadRealTimePricing>[0],
            RealTimePricingModel
        >(loadRealTimePricing);
        const realTimePricing = await awaitableLoadRealTimePricing({
            productPriceParameters: loadRealTimePricingParameter.productPriceParameters,
        })(props.dispatch, props.getState);
        if (realTimePricing) {
            loadRealTimePricingParameter.onSuccess?.(realTimePricing);
        } else {
            loadRealTimePricingParameter.onError?.(realTimePricing);
        }
    } else {
        props.dispatch(loadRealTimePricing(loadRealTimePricingParameter));
    }

    const productSettings = settings.productSettings;
    if (productSettings.canSeePrices && productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => !!props.pricingLoaded);
    }
};

export const LoadRealTimeInventoryExtended: HandlerType = props => {
    if(isBrickSelectorPage()){
        return;
    }
    if (
        !props.result.products?.length ||
        (getIsWebCrawler() &&
            !getSettingsCollection(props.getState()).websiteSettings.includeRealTimeInventoryAndPricingForSeo)
    ) {
        return;
    }

    const {
        result: { productInfosByProductId },
    } = props;

    const productIds = Object.keys(productInfosByProductId).filter(o => !productInfosByProductId[o]?.inventory);
    if (productIds.length === 0) {
        return;
    }

    props.dispatch(
        loadRealTimeInventory({
            productIds,
            onSuccess: realTimeInventory => {
                props.dispatch({
                    type: "Pages/ProductList/CompleteLoadRealTimeInventory",
                    realTimeInventory,
                });
            },
            onError: () => {
                props.dispatch({
                    type: "Pages/ProductList/FailedLoadRealTimeInventory",
                });
            },
            onComplete(realTimeInventoryProps) {
                if (realTimeInventoryProps.apiResult) {
                    this.onSuccess?.(realTimeInventoryProps.apiResult);
                } else if (realTimeInventoryProps.error) {
                    this.onError?.(realTimeInventoryProps.error);
                }
            },
        }),
    );
};

addToChainAfter(chain, SetExpandTokens, AddToExpandTokens);
addToChainAfter(chain, DispatchSetParameter, ProductGroupingApiCall);
replaceInChain(chain, LoadRealTimePrices, LoadRealTimePricesExtended);
replaceInChain(chain, LoadRealTimeInventory, LoadRealTimeInventoryExtended);
